import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { ChunkLoadError, isChunkLoadError } from './chunkLoadError';
import { markAppStale } from './appVersionStore';
import { registerPrefetchLoader } from './chunkPrefetch';

/** 部署換版或網路瞬斷時，第一次失敗後等一下再試一次 */
export const CHUNK_RETRY_DELAY_MS = 800;
const CHUNK_RETRY_COUNT = 1;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;
type ComponentModule<T extends AnyComponent> = { default: T };

interface RetryOptions {
    retryCount?: number;
    retryDelayMs?: number;
    wait?: (delayMs: number) => Promise<void>;
}

function waitForDelay(delayMs: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Vite 的預載輔助函式在 vite:preloadError 被 preventDefault 時，會讓失敗的 import() 靜默變成 undefined，
 * 之後 React.lazy 讀 .default 才爆出難以辨識的 TypeError。這裡把它還原成明確的 chunk 載入錯誤。
 */
async function requireModule<T>(factory: () => Promise<T>): Promise<T> {
    const loadedModule = await factory();
    if (loadedModule === undefined) {
        throw new ChunkLoadError(
            'Importing a module script failed (module resolved to undefined).'
        );
    }
    return loadedModule;
}

/**
 * chunk 取不到時等一下再試（瞬斷會自己好）。重試用完仍失敗，或遇到其他種類的錯誤，
 * 原樣往外丟，由呼叫端決定降級方式。
 */
export async function retryChunkLoad<T>(
    factory: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const { retryCount = CHUNK_RETRY_COUNT, retryDelayMs = CHUNK_RETRY_DELAY_MS } = options;
    const wait = options.wait ?? waitForDelay;

    for (let attempt = 0; ; attempt += 1) {
        try {
            return await requireModule(factory);
        } catch (error) {
            if (!isChunkLoadError(error) || attempt >= retryCount) throw error;
            await wait(retryDelayMs);
        }
    }
}

/**
 * 重試後仍是 chunk 取不到，就標記「前端已過期」並改用 placeholder。
 * 其他種類的錯誤（例如模組執行時丟出的例外）原樣往外丟，維持原本的行為。
 */
export async function loadModuleWithRetry<T extends AnyComponent>(
    factory: () => Promise<ComponentModule<T>>,
    placeholder: T,
    options: RetryOptions = {}
): Promise<ComponentModule<T>> {
    try {
        return await retryChunkLoad(factory, options);
    } catch (error) {
        if (!isChunkLoadError(error)) throw error;
        markAppStale();
        return { default: placeholder };
    }
}

function renderNothing(): null {
    return null;
}

/**
 * 取代 React.lazy：chunk 取不到時不丟錯（丟錯會讓整棵 React 樹卸載、使用者正在填的表單消失），
 * 而是改畫 placeholder 並標記「前端已過期」。路由頁面請傳面板元件；對話框、分頁這類
 * 附屬元件維持預設的「什麼都不畫」，讓背後的頁面原封不動。
 * 每個 factory 同時登記到預載清單，閒置時會被提前載入。
 */
export function lazyWithRetry<T extends AnyComponent>(
    factory: () => Promise<ComponentModule<T>>,
    placeholder: AnyComponent = renderNothing
): LazyExoticComponent<T> {
    registerPrefetchLoader(factory);
    return lazy(() => loadModuleWithRetry(factory, placeholder as T));
}
